import {
  JobWorkType,
  addDrawerListeners,
  arrangeFiltersByOrder,
  commonPropsForSSR,
  filterDropItems,
  getDrawerPopCount,
  getURLSearchParams,
  getfullUrl,
  handleDrawerScroll,
  makeApiCalls,
  makeOverViewText,
  modifyQueryLocation,
  modifySelectedLocation,
} from '@components/common';
import {
  API_NAMES,
  PreSelectedFilterPage,
  WIDGETS_NAME,
  _CONSTANTS,
  allowedSectionsToHide,
  getScrollHeightDrawerExp,
} from '@components/common/constant';
import {
  getFilterQueryString,
  mapQueryParams,
} from '@components/common/filterHelper';
import JobListingContainer from '@components/jobListContainer';
import JobSearchSteps from '@components/jobSearchSteps';
import { RemoteCategoriesMetaTags } from '@components/metaComponent/remoteCategories';
import {
  CountryResponseProps,
  ICompanies,
  IDeviceType,
  IJob,
  SuccessStoriesType,
  cdnEXPTType,
} from '@components/shared.types';
import { fireExperimentEvent } from '@utils/cdnTracking';
import { getJobCategories } from 'API/ApiHelpers';
import {
  getCompaniesHiringCount,
  getFirstJobDetails,
  getJobDetails,
  getSavedJobsId,
} from 'API/jobs';
import { isProdEnv } from 'helper';
import { getOrUpdateGUID } from 'helper/eventTrackingUtils';
import { getCDNExptDetails } from 'helper/experiments/experimentActions';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import Script from 'next/script';
import { useEffect, useState } from 'react';

// ! TODO: Change the data (any) to actual type
type ResponseProps = {
  isLoggedIn: boolean;
  metaIdentifier: string | string[] | undefined;
  isPremiumUser: boolean;
  selectedLocation: string;
  selectedCountry: string;
  countyList: CountryResponseProps[];
  searchedJobTitle?: string;
  isWorkAnywhereInUsChecked?: boolean;
  isWorkAnywhereInWorldChecked?: boolean;
  localization: any;
  saveJobIds: Array<string>;
  jobsData: JobsDataType;
  newsData: any;
  queryParams: { [key: string]: Array<string> | string | undefined };
  successStoriesList: Array<SuccessStoriesType>;
  deviceType: IDeviceType;
  pageName: string;
  selectedFilters: { [key: string]: Array<string> };
  showAllDropFilters?: boolean;
  url: string;
  firstJobDetails: IJob;
  firstJobComDetails: ICompanies;
  isPopularTitle?: boolean;
  userAgent: string;
  cdnExptData?: cdnEXPTType;
};

type Company = {
  accolades: Array<AccoladesType>;
  logo: string;
  name: string;
  slug: string;
};

type JobsType = {
  id: string;
  title: string;
  saved: boolean;
  featured: boolean;
  allowedCandidateLocation: Array<string>;
  company: Company;
  description: string;
  jobLocations: Array<any>;
  jobSchedules: Array<string>;
  jobTypes: Array<string>;
  postedDate: string;
  remoteOptions: Array<any>;
};

type FiltersType = {
  name: string;
  categoryWithCount: CategoryWithCount;
};

type FiltersDataByOrderType = {
  name: string;
  categoryWithCount: CategoryWithCount;
  slug: string;
};

type CategoryWithCount = {
  [key: string]: number;
};

type JobsDataType = {
  jobFilters: Array<FiltersType>;
  jobs: {
    currentPage: number;
    resultPerPage: number;
    totalCount: number;
    totalPages: number;
    results: Array<JobsType>;
  };
};

type AccoladesType = {
  imgSrc: string | undefined;
  title: string | undefined;
  description: string | null;
  accoladeGroups: Array<any>;
  companies: Array<any>;
  accolades: Array<any>;
};

export default function JobSchedulePage(props: Readonly<ResponseProps>) {
  const {
    jobsData,
    queryParams,
    localization,
    successStoriesList,
    newsData,
    isPremiumUser,
    isLoggedIn,
    deviceType,
    metaIdentifier,
    userAgent,
    cdnExptData,
  } = props;
  let jobFilterDataByOrder: Array<FiltersDataByOrderType> =
    arrangeFiltersByOrder(
      jobsData?.jobFilters || [],
      true,
      queryParams,
      deviceType
    );
  let queryParamsByOrder: {
    [key: string]: Array<string> | string | undefined;
  } = arrangeFiltersByOrder(queryParams);
  const [guid, setGuid] = useState<any>(getOrUpdateGUID()); // for event tracking
  const [showJobSearchSteps, setShowJobSearchSteps] = useState(false);
  const [shouldShowSearchStepDrawer, setShouldShowSearchStepDrawer] = useState(
    !isLoggedIn
  );
  const [hiringCount, setHiringCount] = useState();
  const WidgetsToShow = isPremiumUser
    ? [WIDGETS_NAME.NEWS, WIDGETS_NAME.CURRENTLY_HIRING]
    : [
        WIDGETS_NAME.PROMO_BANNER,
        WIDGETS_NAME.NEWS,
        WIDGETS_NAME.CURRENTLY_HIRING,
        WIDGETS_NAME.SUCCESS_STORIES,
        !isLoggedIn && WIDGETS_NAME.NEWS_LETTER,
        WIDGETS_NAME.MPR,
      ];
  const pageTitle = props.pageName.replace('Online', '');
  const breadCrumbNameValue = pageTitle.replace('Jobs', '');

  const getJobsCount = async () => {
    const hiringCount = await getCompaniesHiringCount();
    setHiringCount(hiringCount);
  };

  useEffect(() => {
    const popCount = getDrawerPopCount();

    if (shouldShowSearchStepDrawer && popCount < 5) {
      getJobsCount();
      addDrawerListeners(setShowJobSearchSteps, undefined);
    }

    return () => {
      window.removeEventListener('scroll', () =>
        handleDrawerScroll(getScrollHeightDrawerExp(), setShowJobSearchSteps)
      );
    };
  }, []);

  const breadCrumbNameRC = isPremiumUser
    ? 'job-filter-rc-premium'
    : 'job-filter-rc-public';
  const breadCrumbNameAuth = isPremiumUser
    ? 'job-full-time-auth'
    : 'job-full-time-unauth';

  return (
    <>
      <Head>
        {RemoteCategoriesMetaTags({
          categoryName: props.metaIdentifier || '',
        })}
        {isProdEnv && <link rel="canonical" href={props.url} />}
      </Head>
      <Script
        src="/ui-experimentation/cdn/experiment-sync.min.js"
        strategy="afterInteractive"
      />
      <Script id="experiment-script" strategy="afterInteractive">
        {`
          window.fireExperimentEvent = ${fireExperimentEvent.toString()};
        `}
      </Script>
      {shouldShowSearchStepDrawer && showJobSearchSteps && (
        <JobSearchSteps
          isCloseAllowed={true}
          showJobSearchSteps={showJobSearchSteps}
          setJobSearchStepsState={setShowJobSearchSteps}
          signupAndFind={localization.unlock_banner.signup_and_find}
          mobileView={deviceType === 'mobile'}
          categoryDetails={{ name: breadCrumbNameValue.trim() }}
          jobsData={jobsData}
          hiringCount={hiringCount}
          setShouldShowSearchStepDrawer={setShouldShowSearchStepDrawer}
          screenName="jobs by category"
          isCategoryPage={true}
        />
      )}
      <JobListingContainer
        isLoggedIn={isLoggedIn}
        jobsData={jobsData}
        selectedCountry={props.selectedCountry}
        countyList={props.countyList}
        location={props.selectedLocation}
        searchedJobTitle={props.searchedJobTitle}
        localization={localization}
        newsData={newsData}
        jobFilters={jobFilterDataByOrder}
        queryParamsByOrder={queryParamsByOrder}
        successStoriesList={successStoriesList}
        savedJobsIdsList={props.saveJobIds}
        sectionsToHide={[
          allowedSectionsToHide.CATEGORY_TABS,
          allowedSectionsToHide.WIDGETS,
        ]}
        widgetsName={WidgetsToShow}
        isPremiumUser={isPremiumUser}
        pageName={props.pageName}
        selectedFilters={props.selectedFilters}
        breadCrumbName={
          !props.isPopularTitle
            ? `${breadCrumbNameRC}`
            : `${breadCrumbNameAuth}`
        }
        breadCrumbNameValue={[breadCrumbNameValue || '']}
        deviceType={deviceType}
        showAllDropFilters={props.showAllDropFilters}
        guid={guid}
        setGuid={setGuid}
        isWorkAnywhereInUsChecked={props.isWorkAnywhereInUsChecked}
        isWorkAnywhereInWorldChecked={props.isWorkAnywhereInWorldChecked}
        firstJobDetails={props.firstJobDetails}
        firstJobComDetails={props.firstJobComDetails}
        isLegitimate={true}
        topBanner={{
          showBanner: true,
          bannerTitle: localization.heading.replace(`{0}`, pageTitle || ''),
        }}
        overviewText={makeOverViewText(metaIdentifier || '')}
        JobWorkType={JobWorkType}
        isDynamicBreadcrumb={true}
        isExperiment={
          cdnExptData && cdnExptData?.vID == '3' && !props.isPremiumUser
        }
        envPath={process.env.NEXT_PUBLIC_ROOT_URL}
      />
    </>
  );
}

export const getServerSideProps: GetServerSideProps<ResponseProps> = async ({
  query,
  req,
  resolvedUrl = '',
}: GetServerSidePropsContext) => {
  const jobScheduleId = query.jobScheduleId;
  let pageName = '';
  let selectedFilters = {};
  let countryName = '';
  query = mapQueryParams(query);
  const isSchedulePage = resolvedUrl?.includes('isSchedulePage');
  if (jobScheduleId === 'full-time' || jobScheduleId === 'part-time') {
    pageName = PreSelectedFilterPage[jobScheduleId].title;
    selectedFilters = PreSelectedFilterPage[jobScheduleId].selecteFilter;
    query['jobschedules'] = query.jobschedules || jobScheduleId;
  }

  if (jobScheduleId === 'entry-level') {
    pageName = PreSelectedFilterPage[jobScheduleId].title;
    selectedFilters = PreSelectedFilterPage[jobScheduleId].selecteFilter;
    query['careerlevel'] = query.jobschedules || jobScheduleId;
  }

  let canonicalUrl = jobScheduleId || '';

  if (jobScheduleId === 'part-time' && isSchedulePage) {
    canonicalUrl = '/remote-jobs/part-time';
  }

  if (jobScheduleId === 'full-time' && isSchedulePage) {
    canonicalUrl = '/remote-jobs/full-time';
  }

  if (jobScheduleId === 'entry-level' && isSchedulePage) {
    canonicalUrl = '/remote-jobs/entry-level';
  }
  const fullUrl = getfullUrl(
    req,
    `${isSchedulePage ? '' : '/remote-jobs/'}` + canonicalUrl
  );
  let selectedLocation: string = getURLSearchParams(query, 'joblocations');
  let searchKeyword: string = getURLSearchParams(query, 'searchkeyword');

  const commonSSRProps = await commonPropsForSSR(
    req,
    'jobSchedulePage',
    selectedFilters
  );

  const [countyListServiceRes, newsResponse = {}, successStoriesList = {}] =
    await makeApiCalls([
      API_NAMES.COUNTRY_LIST_EB,
      API_NAMES.NEWS_WIDGET,
      API_NAMES.SUCCESS_STORY_WIDGET,
    ]);

  /**
   *  Below code is to modify the lookup service response remove @@@ and null values
   */
  let { locationObj, isInternationalLocation, locationNameAndState }: any =
    modifySelectedLocation(selectedLocation, countyListServiceRes);
  if (selectedLocation) {
    selectedLocation = locationNameAndState;
    countryName = locationObj.countryName;
    query = modifyQueryLocation(locationObj, isInternationalLocation, query);
  }

  let queryString: string = getFilterQueryString(query);

  const promiseList = [
    getJobDetails(
      `search${queryString.slice(0, -1)}`,
      commonSSRProps.cookieHeader,
      true,
      false,
      req.headers,
      true,
      fullUrl
    ),
    getSavedJobsId(commonSSRProps.isPremiumUser, commonSSRProps.cookieHeader),
    getJobCategories(commonSSRProps.cookieHeader),
  ];

  const [{ data }, saveJobIds, jobsCategories] = await Promise.all<any>(
    promiseList
  );

  // Sort the filters drop items
  if (data?.jobFilters) {
    data.jobFilters = filterDropItems({
      filterData: data?.jobFilters || [],
      categoriesData: jobsCategories.data,
    });
  }

  const firstJobData = await getFirstJobDetails(
    data?.jobs?.results[0],
    commonSSRProps.cookieHeader,
    commonSSRProps.isPremiumUser
  );

  return {
    props: {
      ...commonSSRProps,
      jobsData: data,
      newsData: newsResponse.data || [],
      searchedJobTitle: searchKeyword || '',
      countyList: countyListServiceRes,
      selectedCountry: countryName || '',
      selectedLocation: selectedLocation,
      queryParams: query,
      saveJobIds: saveJobIds || [],
      pageName: pageName || '',
      successStoriesList: successStoriesList.data || [],
      isWorkAnywhereInWorldChecked: true,
      isWorkAnywhereInUsChecked: true,
      deviceType: commonSSRProps.deviceType,
      fullwidth: true,
      showAllDropFilters:
        commonSSRProps.cookies[_CONSTANTS.SET_SHOW_ALL_DROP_FILTERS] === 'true',
      url: fullUrl,
      ...firstJobData,
      metaIdentifier: jobScheduleId,
      userAgent: req?.headers['user-agent'] ?? '',
      cdnExptData: getCDNExptDetails(
        query,
        commonSSRProps?.deviceType,
        commonSSRProps?.cookieHeader
      ),
    },
  };
};
